import { Fragment, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { DivIcon, LatLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button, { ButtonVariantProps } from '../../components/Button';
import ActionGuard from '../../components/ActionGuard/ActionGuard';
import { FitBounds, BoundsListener, MapAutoResize } from '../../components/MapBoundsComponents';
import {
  useGatesWMinRoute, useGateDuplicateAnalysis,
  useMergeGates, useMergeRoutes,
} from '../../hooks/useGates';
import {
  useRoutesForManagement, useUpdateRoute, useRetrieveAddresses, useDeleteRoutesAndGates,
} from '../../hooks/useRoutes';
import { useSetGatePinned, useUpdateGate } from '../../hooks/useGates';
import { useGateSummariesWithCluster, useGateFocus, useMapBounds } from '../../hooks/useGateMap';
import { createGateIcon } from '../../utils/gateMapUtils';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { GateSummaryWithCluster } from '../../types/GateSummary';
import { DuplicateAnalysis, DuplicateGateEntry, DuplicateGroup, Gate } from '../../types/Gate';
import { RouteWithGates } from '../../api/routes';
import {
  HiOutlineCheckCircle, HiOutlineChevronRight, HiOutlinePencil, HiOutlineStar, HiOutlineXMark, HiStar,
} from 'react-icons/hi2';
import { clusterRoutes } from '../../utils/routeClustering';
import {
  ROUTE_MERGE_MAX_DISTANCE_M, RouteMergeCandidates, describeRouteMerge, describeRouteMergeResult,
  findRouteMergeCandidates, suggestMergedRouteName,
} from '../../utils/routeMergeCandidates';
import { routeHasPinnedGate, sortClustersPinnedFirst } from '../../utils/pinnedGates';
import {
  UnusedDeletePlan, describeUnusedDelete, describeUnusedDeleteResult, isGateUnused, isRouteUnused, planIsEmpty,
  planUnusedDelete, routeHasUnused, summariseUnusedDelete, unusedGatesOf,
} from '../../utils/unusedRoutes';
import {
  DUPLICATE_GATE_THRESHOLD_M, DuplicateMembership, describeDuplicateGroup, describeGateMerge,
  describeGateMergeResult, duplicateGroupBounds, duplicateGroupCentres, formatSessionDate, indexDuplicateGroups,
  keptGate,
} from '../../utils/duplicateGates';
import { countSelected, describeSelection, gateIdsOfDuplicateGroups, gateIdsOfRoutes } from '../../utils/routeListView';
import { plural } from '../../utils/plural';

type FilterMode = 'all' | 'merged' | 'unused' | 'duplicates';

/** What the route list says when a filter leaves nothing to show. */
const EMPTY_LIST_MESSAGE: Record<Exclude<FilterMode, 'duplicates'>, string> = {
  all: 'No routes for this team yet.',
  merged: 'No merged routes.',
  unused: 'No unused routes or gates.',
};

/** An action waiting on the in-page confirm: what it says, and what runs on yes. */
interface PendingAction {
  message: ReactNode;
  confirmLabel: string;
  confirmVariant?: ButtonVariantProps['variant'];
  run: () => void;
}

/** A result notice carries counts, so it stays up longer than the toaster's default. */
const RESULT_TOAST = { duration: 8000 };

/** How faint a gate the list is not showing is drawn on the map. */
const FADED_MARKER_OPACITY = 0.35;
/** The ring drawn round each duplicate group on the map, in pixels, so it reads at any zoom. */
const DUPLICATE_RING_RADIUS_PX = 18;
const NO_GATES: ReadonlySet<string> = new Set();

/* ─── Main Component ─── */

export default function RouteManagement(): ReactElement {
  const {
    data: routes,
    isPending,
    isError,
  } = useRoutesForManagement();

  // Gate data still needed for map and duplicate analysis
  const { data: gatesRaw } = useGatesWMinRoute();
  const gateSummaries = useGateSummariesWithCluster(gatesRaw);

  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(
    new Set(),
  );
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [editMode, setEditMode] = useState(false);
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedDupGroups, setExpandedDupGroups] = useState<Set<number>>(
    new Set(),
  );
  // The map area of the duplicate group being looked at, overriding the usual
  // all-gates or one-gate framing while it is set.
  const [groupBounds, setGroupBounds] = useState<LatLngBounds | null>(null);
  const [groupFocusTrigger, setGroupFocusTrigger] = useState(0);
  // The confirm dialog: set while an action waits on a yes, cleared when it closes.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  // Loaded with the page, not just in the Duplicates view: gate rows in the
  // main list carry a badge for the group they are in.
  const {
    data: dupAnalysis,
    isPending: isDupPending,
    isError: isDupError,
  } = useGateDuplicateAnalysis();
  const duplicateIndex = useMemo(() => indexDuplicateGroups(dupAnalysis), [dupAnalysis]);
  const duplicateGroupCount = dupAnalysis?.summary.total_duplicate_groups ?? 0;

  const mergeMutation = useMergeGates();
  const deleteMutation = useDeleteRoutesAndGates();
  const mergeRoutesMutation = useMergeRoutes();
  const updateRouteMutation = useUpdateRoute();
  const updateGateMutation = useUpdateGate();
  const setGatePinnedMutation = useSetGatePinned();
  const retrieveAddressesMutation = useRetrieveAddresses();

  /* ── Duplicate-group merge: the confirm names the gate that stays ── */
  const handleMergeGroup = useCallback(
    (group: DuplicateGroup) => {
      setPendingAction({
        message: describeGateMerge(group),
        confirmLabel: 'Merge',
        run: () => mergeMutation.mutate(group.gates.map((gate) => gate.id), {
          onSuccess: (res) => {
            setExpandedDupGroups(new Set());
            setGroupBounds(null);
            toast.success(describeGateMergeResult(group, res), RESULT_TOAST);
          },
          onError: (err) => {
            console.error('Failed to merge gates:', err);
            toast.error('Failed to merge gates. Please try again.');
          },
        }),
      });
    },
    [mergeMutation],
  );

  /* ── Filter routes ── */
  const filteredRoutes = useMemo(() => {
    if (!routes) return [];
    switch (filterMode) {
      case 'unused':
        // Routes nothing uses, and routes in use that carry a gate nothing uses.
        return routes.filter(routeHasUnused);
      case 'merged':
        return routes.filter(
          (r) =>
            r.gates &&
            r.gates.some((g) => g.creation_mode === 3),
        );
      case 'duplicates':
        // The Duplicates view lists gate groups, not routes.
        return [];
      case 'all':
      default:
        return routes;
    }
  }, [routes, filterMode]);

  const clusteredRoutes = useMemo(
    () => sortClustersPinnedFirst(clusterRoutes(filteredRoutes)),
    [filteredRoutes],
  );
  // Which routes in each cluster can be merged together, for the button on its header.
  const clusterMerges = useMemo(
    () => new Map(clusteredRoutes.map((c) => [c.clusterId, findRouteMergeCandidates(c.routes)])),
    [clusteredRoutes],
  );

  /* ── Filters ──
     A filter change keeps the ticked routes: the action bar says how many of
     them the new filter is hiding. The selection goes when Manage is turned off. */
  const selectFilter = useCallback((mode: FilterMode) => {
    setFilterMode(mode);
    setExpandedDupGroups(new Set());
    setGroupBounds(null);
  }, []);
  const toggleManage = useCallback(() => {
    if (editMode) setSelectedRouteIds(new Set());
    setEditMode(!editMode);
  }, [editMode]);
  const clearSelection = useCallback(() => setSelectedRouteIds(new Set()), []);

  /* ── Edit-mode handlers ── */

  const handleSelectUnused = useCallback(() => {
    if (!routes) return;
    setSelectedRouteIds(new Set(routes.filter(routeHasUnused).map((r) => r.id)));
  }, [routes]);

  /* ── Deleting unused routes and gates ──
     One plan, two entry points: the selected routes, or every route with
     something unused on it. A route goes only when nothing uses it; a route
     in use keeps its place and loses just its unused gates. The plan is what
     the button label, the confirm and the deletion all read, so they agree. */
  const selectedRoutes = useMemo(
    () => (routes ?? []).filter((r) => selectedRouteIds.has(r.id)),
    [routes, selectedRouteIds],
  );
  const selectedDeletePlan = useMemo(() => planUnusedDelete(selectedRoutes), [selectedRoutes]);
  const allUnusedPlan = useMemo(
    () => planUnusedDelete((routes ?? []).filter(routeHasUnused)),
    [routes],
  );

  const handleDeleteUnused = useCallback(
    (plan: UnusedDeletePlan) => {
      if (planIsEmpty(plan)) return;
      setPendingAction({
        message: describeUnusedDelete(plan),
        confirmLabel: 'Delete',
        confirmVariant: 'danger',
        run: () => deleteMutation.mutate(
          { gateIds: plan.gateIds, routeIds: plan.routeIds },
          {
            onSuccess: () => {
              setSelectedRouteIds(new Set());
              toast.success(describeUnusedDeleteResult(plan), RESULT_TOAST);
            },
            onError: (err) => {
              console.error('Failed to delete:', err);
              toast.error('Failed to delete. Please try again.');
            },
          },
        ),
      });
    },
    [deleteMutation],
  );

  /* ── Which of the selected routes can actually be merged ── */
  const mergeSelection = useMemo(() => findRouteMergeCandidates(selectedRoutes), [selectedRoutes]);
  const canMergeSelection = mergeSelection.candidates.length >= 2;
  const mergeDisabledReason = canMergeSelection
    ? undefined
    : `Selected routes are more than ${ROUTE_MERGE_MAX_DISTANCE_M} m apart and cannot be merged`;

  // Merges a set of candidates, whether they came from the ticked routes or a
  // cluster header. The confirm carries a name field for the merged route; the
  // field keeps its own draft and reports it here, so typing does not
  // re-render the page behind the dialog.
  const handleMergeRoutes = useCallback((selection: RouteMergeCandidates) => {
    const { candidates } = selection;
    if (candidates.length < 2) return;
    const ids = candidates.map((r) => r.id);
    const draft = { name: suggestMergedRouteName(candidates) };
    const run = () => {
      const name = draft.name.trim();
      mergeRoutesMutation.mutate({ routeIds: ids, description: name }, {
        onSuccess: (res) => {
          setSelectedRouteIds(new Set());
          const outcome = describeRouteMergeResult(res, ids.length, name);
          if (outcome.ok) toast.success(outcome.text, RESULT_TOAST);
          else toast.error(outcome.text, RESULT_TOAST);
        },
        onError: (err: any) => {
          console.error('Failed to merge routes:', err);
          toast.error(err?.response?.data?.message || 'Failed to merge routes.');
        },
      });
    };
    setPendingAction({
      message: (
        <>
          {describeRouteMerge(selection)}
          <MergedRouteNameField
            initial={draft.name}
            onChange={(name) => { draft.name = name; }}
            onEnter={() => {
              run();
              setPendingAction(null);
            }}
          />
        </>
      ),
      confirmLabel: 'Merge',
      run,
    });
  }, [mergeRoutesMutation]);

  // One route at a time, from its editor. The server fills in missing
  // addresses by itself when the list loads, so this is for looking one up again.
  const handleRetrieveAddress = useCallback((routeId: string) => {
    retrieveAddressesMutation.mutate([routeId], {
      onSuccess: () => toast.success('Address retrieved.'),
      onError: () => toast.error('Failed to retrieve the address.'),
    });
  }, [retrieveAddressesMutation]);
  const retrievingRouteId = retrieveAddressesMutation.isPending
    ? retrieveAddressesMutation.variables?.[0] ?? null
    : null;

  const handleUpdateRoute = useCallback(
    (routeId: string, data: { description: string; notes: string | null }) => {
      updateRouteMutation.mutate(
        { routeId, data },
        { onError: () => toast.error('Failed to update the route.') },
      );
    },
    [updateRouteMutation],
  );

  const handleUpdateGate = useCallback(
    (gateId: string, data: Pick<Partial<Gate>, 'name' | 'notes'>) => {
      updateGateMutation.mutate(
        { gateId, data },
        { onError: () => toast.error('Failed to update the gate.') },
      );
    },
    [updateGateMutation],
  );

  const handleSetGatePinned = useCallback(
    (gateId: string, pinned: boolean) => {
      setGatePinnedMutation.mutate(
        { gateId, pinned },
        { onError: () => toast.error('Failed to update the pinned gate.') },
      );
    },
    [setGatePinnedMutation],
  );

  /* ── Selection toggles ── */
  const toggleRouteSelection = useCallback((routeId: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  const toggleCluster = useCallback((clusterId: number) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  }, []);

  /* ── Map ── */
  const {
    activeGateId,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  } = useGateFocus(gateSummaries);

  const { bounds, mapCenter, showBothGates, handleMapBoundsChange } =
    useMapBounds(
      gateSummaries,
      selectedGate,
      selectedGate ? 'selected' : 'all',
    );

  // Framing a gate or every gate drops the group framing; opening a duplicate
  // group sets it.
  const focusGate = useCallback((gateId: string) => {
    setGroupBounds(null);
    handleFocusGate(gateId);
  }, [handleFocusGate]);
  const zoomAll = useCallback(() => {
    setGroupBounds(null);
    handleZoomAll();
  }, [handleZoomAll]);
  const showGroupOnMap = useCallback((group: DuplicateGroup) => {
    const area = duplicateGroupBounds(group);
    if (!area) return;
    setGroupBounds(area);
    setGroupFocusTrigger((n) => n + 1);
  }, []);
  const toggleDupGroup = useCallback((groupIndex: number) => {
    const opening = !expandedDupGroups.has(groupIndex);
    setExpandedDupGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIndex)) next.delete(groupIndex);
      else next.add(groupIndex);
      return next;
    });
    const group = dupAnalysis?.duplicate_groups[groupIndex];
    if (opening && group) showGroupOnMap(group);
  }, [expandedDupGroups, dupAnalysis, showGroupOnMap]);
  // From a gate row's badge: straight to the Duplicates view with that group open.
  const openDuplicateGroup = useCallback((groupIndex: number) => {
    selectFilter('duplicates');
    setExpandedDupGroups(new Set([groupIndex]));
    const group = dupAnalysis?.duplicate_groups[groupIndex];
    if (group) showGroupOnMap(group);
  }, [selectFilter, dupAnalysis, showGroupOnMap]);

  // The number each gate carries on the map, so the list can show the same one.
  const gateOrder = useMemo(
    () => new Map(gateSummaries.map((summary) => [summary.id, summary.order])),
    [gateSummaries],
  );

  /* ── The map follows the list ──
     Gates the current view is not listing are faded; gates on a ticked route
     stand out; each duplicate group gets a ring in the Duplicates view. */
  const shownGateIds = useMemo<ReadonlySet<string> | null>(() => {
    if (filterMode === 'all') return null;
    if (filterMode === 'duplicates') return gateIdsOfDuplicateGroups(dupAnalysis);
    return gateIdsOfRoutes(filteredRoutes);
  }, [filterMode, filteredRoutes, dupAnalysis]);
  const highlightedGateIds = useMemo<ReadonlySet<string>>(
    () => (editMode && selectedRoutes.length ? gateIdsOfRoutes(selectedRoutes) : NO_GATES),
    [editMode, selectedRoutes],
  );
  const shownSelectedCount = countSelected(filteredRoutes, selectedRouteIds);

  // One icon per look, kept across renders: a new icon object would make
  // Leaflet rebuild every marker on every render of the page.
  const iconCache = useMemo(() => new Map<string, DivIcon>(), []);
  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string, highlighted: boolean) => {
      const key = `${type}|${label}|${highlighted}`;
      let icon = iconCache.get(key);
      if (!icon) {
        icon = createGateIcon(type, label, markerIconPng, endMarkerIconPng, { highlighted });
        iconCache.set(key, icon);
      }
      return icon;
    },
    [iconCache],
  );

  /* ── Render ── */
  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Management" isLoading={isPending} />

      {/* Side by side from lg up: routes on the left, the map on the right and
          sticky so it stays in view while the list scrolls. Stacked below lg. */}
      <div className="cr-mt-6 cr-grid cr-gap-6 lg:cr-grid-cols-2 lg:cr-items-start">
        {/* ── Map ── */}
        <div
          className={
            'cr-relative cr-h-[500px] cr-overflow-hidden ' +
            'cr-rounded-xl cr-bg-white cr-shadow-sm ' +
            'lg:cr-sticky lg:cr-top-4 lg:cr-order-2 lg:cr-h-[calc(100vh-8rem)]'
          }
        >
          {isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              <Spinner isFetching={true} />
            </div>
          )}
          {isError && !isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center ' +
                'cr-justify-center cr-text-red-600'
              }
            >
              Unable to load routes. Please try again later.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length === 0 && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              No gates available for this team yet.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000] cr-flex cr-gap-2">
                  <GateStarButton
                    withLabel
                    pinned={selectedGate.gate.pinned}
                    onToggle={() => handleSetGatePinned(selectedGate.gate.id, !selectedGate.gate.pinned)}
                  />
                  {gateSummaries.length > 1 && (
                    <button
                      type="button"
                      onClick={zoomAll}
                      className={
                        'cr-rounded-md cr-bg-white cr-px-3 cr-py-1 ' +
                        'cr-text-xs cr-font-semibold cr-text-blue-900 ' +
                        'cr-shadow-sm hover:cr-bg-cirrusBlue-25'
                      }
                    >
                      All Gates
                    </button>
                  )}
                </div>
              )}
              <MapContainer
                center={mapCenter}
                zoom={13}
                scrollWheelZoom
                className="cr-h-full cr-w-full"
              >
                <MapAutoResize />
                <FitBounds bounds={groupBounds ?? bounds} trigger={fitBoundsTrigger + groupFocusTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution={
                    '&copy; <a href="https://www.openstreetmap.org/' +
                    'copyright">OpenStreetMap</a> contributors'
                  }
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {filterMode === 'duplicates' && (dupAnalysis?.duplicate_groups ?? []).map((group, index) => {
                  const { start, end } = duplicateGroupCentres(group);
                  const isOpen = expandedDupGroups.has(index);
                  const ring = {
                    radius: DUPLICATE_RING_RADIUS_PX,
                    interactive: false,
                    pathOptions: {
                      color: '#d97706', weight: isOpen ? 3 : 2, fillColor: '#fbbf24', fillOpacity: isOpen ? 0.35 : 0.15,
                    },
                  };
                  const key = keptGate(group).id;
                  return (
                    <Fragment key={key}>
                      {start && <CircleMarker center={start} {...ring} />}
                      {showBothGates && end && <CircleMarker center={end} {...ring} />}
                    </Fragment>
                  );
                })}
                {gateSummaries.map((marker) => {
                  const shown = !shownGateIds || shownGateIds.has(marker.id);
                  const highlighted = highlightedGateIds.has(marker.id);
                  const look = {
                    opacity: shown ? 1 : FADED_MARKER_OPACITY,
                    zIndexOffset: highlighted ? 1000 : shown ? 0 : -1000,
                  };
                  return (
                    <Fragment key={marker.id}>
                      <Marker
                        position={marker.start}
                        icon={getGateIcon('start', String(marker.order), highlighted)}
                        {...look}
                      >
                        <GatePopup marker={marker} label="Start gate" onSetPinned={handleSetGatePinned} />
                      </Marker>
                      {showBothGates && marker.end && (
                        <Marker
                          position={marker.end}
                          icon={getGateIcon('end', String(marker.order), highlighted)}
                          {...look}
                        >
                          <GatePopup marker={marker} label="End gate" onSetPinned={handleSetGatePinned} />
                        </Marker>
                      )}
                    </Fragment>
                  );
                })}
              </MapContainer>
            </>
          )}
        </div>

        {/* ── Filter bar + Edit ── */}
        <div className="cr-min-w-0 cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm lg:cr-order-1">
          <div
            className={
              'cr-mb-4 cr-flex cr-flex-wrap cr-items-center cr-justify-between cr-gap-3'
            }
          >
            <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-6">
              <FilterRadio value="all" label="All" current={filterMode} onChange={() => selectFilter('all')} />
              <FilterRadio
                value="merged"
                label="Merged"
                current={filterMode}
                onChange={() => selectFilter('merged')}
              />
              <FilterRadio
                value="unused"
                label="Unused"
                current={filterMode}
                onChange={() => selectFilter('unused')}
              />
              <FilterRadio
                value="duplicates"
                label={duplicateGroupCount > 0 ? `Duplicates (${duplicateGroupCount})` : 'Duplicates'}
                current={filterMode}
                onChange={() => selectFilter('duplicates')}
              />
              <Button
                onClick={toggleManage}
                className={
                  editMode
                    ? 'cr-bg-cirrusBlue-100 cr-text-white'
                    : ''
                }
              >
                Manage
              </Button>
            </div>

            {/* ── Edit-mode action buttons ── */}
            <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
              {editMode && selectedRouteIds.size > 0 && (
                <span
                  data-testid="selection-summary"
                  className="cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-gray-600"
                >
                  {describeSelection(selectedRouteIds.size, shownSelectedCount)}
                  <Button size="sm" onClick={clearSelection} title="Untick every route">
                    Clear
                  </Button>
                </span>
              )}
              {editMode && selectedRouteIds.size >= 2 && (
                <Button
                  onClick={() => handleMergeRoutes(mergeSelection)}
                  disabled={mergeRoutesMutation.isPending || !canMergeSelection}
                  title={mergeDisabledReason}
                >
                  {mergeRoutesMutation.isPending
                    ? 'Merging...'
                    : `Merge Selected (${mergeSelection.candidates.length}/${selectedRouteIds.size})`}
                </Button>
              )}
              {editMode && (
                <Button onClick={handleSelectUnused}>
                  Select Unused
                </Button>
              )}
              {editMode && selectedRouteIds.size > 0 && (
                <Button
                  onClick={() => handleDeleteUnused(selectedDeletePlan)}
                  disabled={deleteMutation.isPending || planIsEmpty(selectedDeletePlan)}
                  title={planIsEmpty(selectedDeletePlan) ? 'Nothing selected is unused' : undefined}
                  className="cr-bg-red-600 cr-text-white hover:cr-bg-red-700"
                >
                  {deleteMutation.isPending
                    ? 'Deleting...'
                    : `Delete Selected (${summariseUnusedDelete(selectedDeletePlan)})`}
                </Button>
              )}
              {/* The Unused filter lists exactly what this removes, Manage or not. */}
              {filterMode === 'unused' && !planIsEmpty(allUnusedPlan) && (
                <Button
                  onClick={() => handleDeleteUnused(allUnusedPlan)}
                  disabled={deleteMutation.isPending}
                  className="cr-bg-red-600 cr-text-white hover:cr-bg-red-700"
                >
                  {deleteMutation.isPending
                    ? 'Deleting...'
                    : `Delete All Unused (${summariseUnusedDelete(allUnusedPlan)})`}
                </Button>
              )}
            </div>
          </div>

          {/* ── Duplicate analysis panel ── */}
          {filterMode === 'duplicates' && (
            <DuplicateAnalysisPanel
              dupAnalysis={dupAnalysis ?? null}
              isPending={isDupPending}
              isError={isDupError}
              expandedGroups={expandedDupGroups}
              gateOrder={gateOrder}
              activeGateId={activeGateId}
              onToggleGroup={toggleDupGroup}
              onFocusGate={focusGate}
              onMergeGroup={handleMergeGroup}
              isMerging={mergeMutation.isPending}
            />
          )}

          {filterMode !== 'duplicates' && !isPending && !isError && clusteredRoutes.length === 0 && (
            <p className="cr-py-8 cr-text-center cr-text-sm cr-text-gray-500">
              {EMPTY_LIST_MESSAGE[filterMode]}
            </p>
          )}

          {/* ── Route-centric list ── */}
          {filterMode !== 'duplicates' && (
            <div className="cr-flex cr-flex-col cr-gap-2">
              {clusteredRoutes.map((cluster) => {
                const isClusterExpanded = expandedClusters.has(
                  cluster.clusterId,
                );
                const selectedInCluster = editMode ? countSelected(cluster.routes, selectedRouteIds) : 0;
                return (
                  <div
                    key={cluster.clusterId}
                    className={
                      'cr-overflow-hidden cr-rounded-lg ' +
                      'cr-border cr-border-gray-200'
                    }
                  >
                    {/* Cluster header: the expand toggle, and in Manage mode a
                        merge of the routes here, since a cluster is one place. */}
                    <div className="cr-flex cr-items-center cr-bg-gray-50 cr-transition-colors hover:cr-bg-gray-100">
                      <button
                        onClick={() => toggleCluster(cluster.clusterId)}
                        className="cr-flex cr-flex-1 cr-items-center cr-gap-2 cr-p-4 cr-text-left"
                      >
                        <HiOutlineChevronRight
                          className={
                            'cr-h-5 cr-w-5 cr-text-gray-500 cr-transition-transform' +
                            (isClusterExpanded ? ' cr-rotate-90' : '')
                          }
                        />
                        <span className="cr-text-2xl cr-font-semibold">
                          {cluster.clusterName}
                        </span>
                        <span className="cr-text-lg cr-text-gray-500">
                          ({cluster.routes.length} route
                          {cluster.routes.length !== 1 ? 's' : ''})
                        </span>
                        {/* The ticks are out of sight while the cluster is collapsed. */}
                        {selectedInCluster > 0 && (
                          <span
                            className={
                              'cr-rounded-full cr-bg-cirrusBlue-100 cr-px-2 cr-py-0.5 ' +
                              'cr-text-xs cr-font-semibold cr-text-blue-900'
                            }
                          >
                            {selectedInCluster} selected
                          </span>
                        )}
                      </button>
                      {editMode && cluster.routes.length > 1 && (
                        <div className="cr-px-4">
                          <ClusterMergeButton
                            merge={clusterMerges.get(cluster.clusterId)}
                            total={cluster.routes.length}
                            isPending={mergeRoutesMutation.isPending}
                            onMerge={handleMergeRoutes}
                          />
                        </div>
                      )}
                    </div>

                    {/* Routes inside cluster */}
                    {isClusterExpanded && (
                      <div className="cr-border-t cr-border-gray-200">
                        {cluster.routes.map((route) => (
                          <RouteRow
                            key={route.id}
                            route={route}
                            isSelected={selectedRouteIds.has(route.id)}
                            editMode={editMode}
                            activeGateId={activeGateId}
                            onToggleSelect={() =>
                              toggleRouteSelection(route.id)
                            }
                            gateOrder={gateOrder}
                            duplicateIndex={duplicateIndex}
                            onFocusGate={focusGate}
                            onShowDuplicates={openDuplicateGroup}
                            onUpdateRoute={(data) => handleUpdateRoute(route.id, data)}
                            onRetrieveAddress={() => handleRetrieveAddress(route.id)}
                            isRetrievingAddress={route.id === retrievingRouteId}
                            onUpdateGate={handleUpdateGate}
                            onSetGatePinned={handleSetGatePinned}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {pendingAction && (
        <ActionGuard
          open
          message={pendingAction.message}
          confirmLabel={pendingAction.confirmLabel}
          confirmVariant={pendingAction.confirmVariant}
          cancelLabel="Cancel"
          actionFn={pendingAction.run}
          onClose={() => setPendingAction(null)}
        />
      )}
    </div>
  );
}

/* ─── Sub-components ─── */

function FilterRadio({
  value,
  label,
  current,
  onChange,
}: {
  value: string;
  label: string;
  current: string;
  onChange: () => void;
}) {
  return (
    <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
      <input
        type="radio"
        name="gateFilter"
        value={value}
        checked={current === value}
        onChange={onChange}
        className={
          'cr-h-4 cr-w-4 cr-border-gray-300 ' +
          'cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100'
        }
      />
      <span className="cr-text-sm cr-font-medium">{label}</span>
    </label>
  );
}

/** The pencil that opens a route's or gate's name-and-notes editor. */
function EditPencil({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      aria-label={title}
      className="cr-rounded cr-p-1 cr-text-blue-900 hover:cr-bg-gray-200"
    >
      <HiOutlinePencil className="cr-h-4 cr-w-4" />
    </button>
  );
}

/**
 * Inline editor for a name and its notes. Enter in the name field or the tick saves,
 * Escape or the cross cancels. Empty notes save as null; clicks never reach the row underneath.
 * `extra` sits under the notes: a route's editor puts its address lookup there.
 */
function NameNotesForm({
  name,
  notes,
  nameLabel,
  namePlaceholder,
  nameMaxLength,
  extra,
  onSave,
  onCancel,
}: {
  name: string;
  notes: string | null;
  nameLabel: string;
  namePlaceholder: string;
  nameMaxLength: number;
  extra?: ReactNode;
  onSave: (name: string, notes: string | null) => void;
  onCancel: () => void;
}) {
  const [nameDraft, setNameDraft] = useState(name);
  const [notesDraft, setNotesDraft] = useState(notes ?? '');

  const save = () => onSave(nameDraft.trim(), notesDraft.trim() || null);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };
  const inputClass = 'cr-w-full cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm';

  return (
    <div
      className="cr-flex cr-flex-col cr-gap-2 cr-rounded-md cr-bg-gray-50 cr-p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <label className="cr-text-xs cr-font-semibold cr-text-gray-700">
        {nameLabel}
        <input
          type="text"
          value={nameDraft}
          maxLength={nameMaxLength}
          onChange={(e) => setNameDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            onKeyDown(e);
          }}
          autoFocus
          placeholder={namePlaceholder}
          className={inputClass + ' cr-mt-1 cr-font-normal'}
        />
      </label>
      <label className="cr-text-xs cr-font-semibold cr-text-gray-700">
        Notes
        <textarea
          value={notesDraft}
          maxLength={1024}
          rows={2}
          onChange={(e) => setNotesDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Notes..."
          className={inputClass + ' cr-mt-1 cr-font-normal'}
        />
      </label>
      {extra}
      <div className="cr-flex cr-gap-2">
        <button type="button" onClick={save} title="Save" aria-label="Save" className="cr-text-green-700">
          <HiOutlineCheckCircle className="cr-h-6 cr-w-6" />
        </button>
        <button type="button" onClick={onCancel} title="Cancel" aria-label="Cancel" className="cr-text-gray-600">
          <HiOutlineXMark className="cr-h-6 cr-w-6" />
        </button>
      </div>
    </div>
  );
}

/**
 * A route's address and a button to look it up again from the route's
 * position. Lives in the route's editor: the address is what names a cluster
 * and a route with no name, so it belongs with the rest of the route's labels.
 */
function RouteAddressLookup({
  address,
  isPending,
  onLookUp,
}: {
  address: string;
  isPending: boolean;
  onLookUp: () => void;
}) {
  return (
    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-text-xs cr-text-gray-600">
      <span>Address: {address || 'none yet'}</span>
      <Button
        size="sm"
        disabled={isPending}
        onClick={onLookUp}
        title="Look the address up again from where the route is"
      >
        {isPending ? 'Looking up...' : 'Look up address'}
      </Button>
    </div>
  );
}

/**
 * The name field inside the route-merge confirm. It holds its own draft and
 * reports each change, so the page behind the dialog does not re-render while
 * the user types. Enter confirms, as it does in the inline name editor.
 */
function MergedRouteNameField({
  initial,
  onChange,
  onEnter,
}: {
  initial: string;
  onChange: (name: string) => void;
  onEnter: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <label className="cr-mt-4 cr-block cr-text-xs cr-font-semibold cr-text-gray-700">
      Name for the merged route
      <input
        type="text"
        value={value}
        maxLength={1024}
        autoFocus
        placeholder="Leave blank to name it by today's date"
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onEnter();
        }}
        className="cr-mt-1 cr-w-full cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm cr-font-normal"
      />
    </label>
  );
}

/**
 * The merge for one cluster: the routes here that overlap each other, offered
 * from the header so the common case — several passes of one road — takes one
 * click rather than a checkbox per route. Greyed, with the reason, when the
 * routes here are too far apart to merge at all.
 */
function ClusterMergeButton({
  merge,
  total,
  isPending,
  onMerge,
}: {
  merge: RouteMergeCandidates | undefined;
  total: number;
  isPending: boolean;
  onMerge: (merge: RouteMergeCandidates) => void;
}) {
  const count = merge?.candidates.length ?? 0;
  if (!merge || count < 2) {
    return (
      <Button
        size="sm"
        disabled
        title={`Routes here are more than ${ROUTE_MERGE_MAX_DISTANCE_M} m apart and cannot be merged`}
      >
        Merge routes
      </Button>
    );
  }
  const leftOut = total - count;
  const title = leftOut > 0
    ? `${leftOut} of the ${total} routes here ${leftOut === 1 ? 'is' : 'are'} more than ` +
      `${ROUTE_MERGE_MAX_DISTANCE_M} m from the others and will be left out`
    : `Merge these ${count} routes into one averaged route`;
  return (
    <Button size="sm" disabled={isPending} title={title} onClick={() => onMerge(merge)}>
      {isPending ? 'Merging...' : `Merge ${count} routes`}
    </Button>
  );
}

/** Grey tag on a route or gate nothing uses, so what Delete would remove reads at a glance. */
function UnusedPill({ title }: { title: string }) {
  return (
    <span
      title={title}
      className="cr-rounded-full cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-gray-600"
    >
      Unused
    </span>
  );
}

/** A gate's map number, drawn like the start-gate marker label so the two read as one. */
function GateNumber({ order }: { order: number | undefined }) {
  if (order == null) return null;
  return (
    <span
      title={`Gate ${order} on the map`}
      className={
        'cr-inline-flex cr-min-w-[1.5rem] cr-items-center cr-justify-center cr-rounded-full ' +
        'cr-bg-teal-700 cr-px-1.5 cr-py-0.5 cr-text-xs cr-font-bold cr-leading-none cr-text-white'
      }
    >
      {order}
    </span>
  );
}

/**
 * Star toggle for one gate pair. Starring is stored per gate (`gates.pinned`); a route
 * shows as starred when any of its gates is. With `withLabel` it renders as a labelled
 * button for the map overlay, otherwise as a bare icon for rows and popups.
 */
function GateStarButton({
  pinned,
  onToggle,
  withLabel = false,
}: {
  pinned: boolean;
  onToggle: () => void;
  withLabel?: boolean;
}) {
  const icon = pinned ? (
    <HiStar className="cr-h-4 cr-w-4 cr-text-amber-400" />
  ) : (
    <HiOutlineStar className="cr-h-4 cr-w-4 cr-text-gray-400 group-hover:cr-text-amber-400" />
  );
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={pinned ? 'Unstar this gate' : 'Star this gate (and its route) for quick selection'}
      aria-label={pinned ? 'Unstar gate' : 'Star gate'}
      aria-pressed={pinned}
      className={
        'cr-group' +
        (withLabel
          ? ' cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-bg-white cr-px-3 cr-py-1 ' +
            'cr-text-xs cr-font-semibold cr-text-blue-900 cr-shadow-sm hover:cr-bg-cirrusBlue-25'
          : '')
      }
    >
      {icon}
      {withLabel && (pinned ? 'Starred' : 'Star')}
    </button>
  );
}

function GatePopup({
  marker,
  label,
  onSetPinned,
}: {
  marker: GateSummaryWithCluster;
  label: string;
  onSetPinned: (gateId: string, pinned: boolean) => void;
}) {
  return (
    <Popup>
      <div className="cr-min-w-[180px]">
        <div className="cr-flex cr-items-center cr-gap-2">
          <GateStarButton
            pinned={marker.gate.pinned}
            onToggle={() => onSetPinned(marker.gate.id, !marker.gate.pinned)}
          />
          <p className="cr-font-semibold">{marker.gate.name || marker.popupLabel}</p>
        </div>
        <p className="cr-text-xs cr-text-gray-500">
          Gate #{marker.order}
        </p>
        <p className="cr-text-xs cr-text-gray-500">{label}</p>
        <p className="cr-text-sm cr-text-gray-600">
          {marker.isValid ? 'Valid' : 'Not yet validated'}
        </p>
        {marker.gate.sessions && marker.gate.sessions.length > 0 && (
          <div className="cr-mt-2 cr-border-t cr-pt-2">
            <p className="cr-text-xs cr-font-semibold">Sessions:</p>
            {marker.gate.sessions.slice(0, 3).map((s, i) => (
              <p key={i} className="cr-text-xs cr-text-gray-600">
                {formatSessionDate(s.date)} -{' '}
                {s.description}
              </p>
            ))}
            {marker.gate.sessions.length > 3 && (
              <p className="cr-text-xs cr-italic cr-text-gray-500">
                ...and {marker.gate.sessions.length - 3} more
              </p>
            )}
          </div>
        )}
      </div>
    </Popup>
  );
}

function RouteRow({
  route,
  isSelected,
  editMode,
  activeGateId,
  gateOrder,
  duplicateIndex,
  onToggleSelect,
  onFocusGate,
  onShowDuplicates,
  onUpdateRoute,
  onRetrieveAddress,
  isRetrievingAddress,
  onUpdateGate,
  onSetGatePinned,
}: {
  route: RouteWithGates;
  isSelected: boolean;
  editMode: boolean;
  activeGateId: string | null;
  gateOrder: Map<string, number>;
  duplicateIndex: Map<string, DuplicateMembership>;
  onToggleSelect: () => void;
  onFocusGate: (gateId: string) => void;
  onShowDuplicates: (groupIndex: number) => void;
  onUpdateRoute: (data: { description: string; notes: string | null }) => void;
  onRetrieveAddress: () => void;
  isRetrievingAddress: boolean;
  onUpdateGate: (gateId: string, data: Pick<Partial<Gate>, 'name' | 'notes'>) => void;
  onSetGatePinned: (gateId: string, pinned: boolean) => void;
}) {
  const routeUnused = isRouteUnused(route);
  // Only worth saying on a route that stays: on an unused route every gate goes with it.
  const unusedGateCount = routeUnused ? 0 : unusedGatesOf(route).length;
  const [isEditing, setIsEditing] = useState(false);

  const save = (description: string, notes: string | null) => {
    if (description !== (route.description ?? '') || notes !== (route.notes ?? null)) {
      onUpdateRoute({ description, notes });
    }
    setIsEditing(false);
  };

  return (
    <div
      className={
        'cr-border-b cr-border-gray-100 last:cr-border-b-0' +
        (isSelected ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      {/* Route header */}
      <div
        className={
          'cr-flex cr-items-center cr-p-4 ' +
          'cr-transition-colors hover:cr-bg-gray-50'
        }
      >
        <div className="cr-flex cr-items-center cr-gap-3">
          {editMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className={
                'cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 ' +
                'cr-text-cirrusBlue-100 ' +
                'focus:cr-ring-cirrusBlue-100'
              }
            />
          )}
          <span className="cr-font-medium">
            {route.description || route.address}
          </span>
          {!isEditing && <EditPencil onClick={() => setIsEditing(true)} title="Edit route name and notes" />}
          {routeUnused && <UnusedPill title="No session uses this route or its gates" />}
          {routeHasPinnedGate(route) && (
            <HiStar
              className="cr-h-4 cr-w-4 cr-text-amber-400"
              title="Has a pinned gate"
            />
          )}
          <span className="cr-text-sm cr-text-gray-500">
            {route.gate_count} gate
            {route.gate_count !== 1 ? 's' : ''}
            {unusedGateCount > 0 && ` · ${unusedGateCount} unused`}
          </span>
          <span className="cr-text-sm cr-text-gray-500">
            {route.total_session_count} session
            {route.total_session_count !== 1 ? 's' : ''}
          </span>
          {route.average_count != null &&
            route.average_count > 1 && (
              <span
                className={
                  'cr-rounded-full cr-bg-blue-100 cr-px-2 ' +
                  'cr-py-0.5 cr-text-xs cr-font-medium ' +
                  'cr-text-blue-800'
                }
              >
                avg: {route.average_count}
              </span>
            )}
        </div>
      </div>

      {/* Route name and notes editor (pencil), or the notes when there are any */}
      {isEditing ? (
        <div className="cr-px-4 cr-pb-3 cr-pl-12">
          <NameNotesForm
            name={route.description ?? ''}
            notes={route.notes ?? null}
            nameLabel="Route name"
            namePlaceholder={route.address || 'Route name...'}
            nameMaxLength={1024}
            extra={
              <RouteAddressLookup
                address={route.address}
                isPending={isRetrievingAddress}
                onLookUp={onRetrieveAddress}
              />
            }
            onSave={save}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : route.notes && (
        <p className="cr-whitespace-pre-line cr-px-4 cr-pb-3 cr-pl-12 cr-text-xs cr-text-gray-500">
          Notes: {route.notes}
        </p>
      )}

      {/* Gate list — always shown; most routes hold one gate, so a per-route
          expander cost a click without saving meaningful space. */}
      {route.gates && route.gates.length > 0 && (
        <div className="cr-border-t cr-border-gray-100 cr-pl-10">
          {route.gates.map((gate) => (
            <GateRow
              key={gate.id}
              gate={gate}
              order={gateOrder.get(gate.id)}
              isActive={gate.id === activeGateId}
              duplicate={duplicateIndex.get(gate.id)}
              onFocus={() => onFocusGate(gate.id)}
              onShowDuplicates={onShowDuplicates}
              onUpdate={(data) => onUpdateGate(gate.id, data)}
              onTogglePinned={() =>
                onSetGatePinned(gate.id, !gate.pinned)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GateRow({
  gate,
  order,
  isActive,
  duplicate,
  onFocus,
  onShowDuplicates,
  onUpdate,
  onTogglePinned,
}: {
  gate: Gate;
  order: number | undefined;
  isActive: boolean;
  duplicate: DuplicateMembership | undefined;
  onFocus: () => void;
  onShowDuplicates: (groupIndex: number) => void;
  onUpdate: (data: Pick<Partial<Gate>, 'name' | 'notes'>) => void;
  onTogglePinned: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const save = (name: string, notes: string | null) => {
    const nextName = name || null;
    if (nextName !== (gate.name ?? null) || notes !== (gate.notes ?? null)) {
      onUpdate({ name: nextName, notes });
    }
    setIsEditing(false);
  };

  const bearing =
    gate.route_bearing != null
      ? gate.route_bearing < 0
        ? Math.round(gate.route_bearing + 360)
        : Math.round(gate.route_bearing)
      : null;

  return (
    <div
      onClick={onFocus}
      className={
        'cr-flex cr-cursor-pointer cr-items-center ' +
        'cr-justify-between cr-border-b cr-border-gray-50 cr-p-3 ' +
        'cr-transition-colors last:cr-border-b-0 hover:cr-bg-gray-50' +
        (isActive ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      <div className="cr-min-w-0 cr-flex-1">
        <div className="cr-flex cr-items-center cr-gap-2">
          <GateNumber order={order} />
          <GateStarButton pinned={gate.pinned} onToggle={onTogglePinned} />
          <span className={'cr-text-sm cr-font-medium' + (gate.name ? '' : ' cr-text-gray-500')}>
            {gate.name || `Gate ${gate.id.slice(0, 8)}...`}
          </span>
          {!isEditing && <EditPencil onClick={() => setIsEditing(true)} title="Edit gate name and notes" />}
          {isGateUnused(gate) && <UnusedPill title="No session uses this gate" />}
          {duplicate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShowDuplicates(duplicate.groupIndex);
              }}
              title={
                `One of ${duplicate.size} gates within ${DUPLICATE_GATE_THRESHOLD_M} m of each other. ` +
                'Open the Duplicates view at this group.'
              }
              className={
                'cr-rounded-full cr-bg-amber-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ' +
                'cr-text-amber-800 hover:cr-bg-amber-200'
              }
            >
              duplicate ×{duplicate.size}
            </button>
          )}
        </div>
        {isEditing && (
          <div className="cr-my-2">
            <NameNotesForm
              name={gate.name ?? ''}
              notes={gate.notes ?? null}
              nameLabel="Gate name"
              namePlaceholder="Gate name..."
              nameMaxLength={128}
              onSave={save}
              onCancel={() => setIsEditing(false)}
            />
          </div>
        )}
        <p className="cr-text-xs cr-text-gray-500">
          Distance:{' '}
          {gate.route_distance != null
            ? `${Math.round(gate.route_distance)}m`
            : 'N/A'}
          {' | '}Bearing: {bearing != null ? `${bearing}°` : 'N/A'}
          {' | '}Elevation:{' '}
          {gate.elevation_difference?.toFixed(2) ?? 'N/A'}m
        </p>
        {!isEditing && gate.notes && (
          <p className="cr-whitespace-pre-line cr-text-xs cr-text-gray-500">Notes: {gate.notes}</p>
        )}
        {gate.sessions && gate.sessions.length > 0 && (
          <div className="cr-mt-1">
            <p className="cr-text-xs cr-font-semibold cr-text-gray-700">
              {gate.sessions.length} session
              {gate.sessions.length !== 1 ? 's' : ''}:
            </p>
            <div className="cr-pl-2">
              {gate.sessions.slice(0, 3).map((s, i) => (
                <p key={i} className="cr-text-xs cr-text-gray-600">
                  {formatSessionDate(s.date)} -{' '}
                  {s.description}
                </p>
              ))}
              {gate.sessions.length > 3 && (
                <p className="cr-text-xs cr-italic cr-text-gray-500">
                  ...and {gate.sessions.length - 3} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onFocus();
        }}
      >
        Center
      </Button>
    </div>
  );
}

/* ─── Duplicate Analysis Sub-component ─── */

function DuplicateAnalysisPanel({
  dupAnalysis,
  isPending,
  isError,
  expandedGroups,
  gateOrder,
  activeGateId,
  onToggleGroup,
  onFocusGate,
  onMergeGroup,
  isMerging,
}: {
  dupAnalysis: DuplicateAnalysis | null;
  isPending: boolean;
  isError: boolean;
  expandedGroups: Set<number>;
  gateOrder: Map<string, number>;
  activeGateId: string | null;
  onToggleGroup: (idx: number) => void;
  onFocusGate: (gateId: string) => void;
  onMergeGroup: (group: DuplicateGroup) => void;
  isMerging: boolean;
}) {
  if (isPending) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-py-12">
        <Spinner isFetching={true} />
        <span className="cr-ml-3 cr-text-sm cr-text-gray-500">
          Analysing gates for duplicates...
        </span>
      </div>
    );
  }

  if (isError || !dupAnalysis) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-red-600">
        Failed to load duplicate analysis. Please try again.
      </div>
    );
  }

  const { duplicate_groups: groups, summary } = dupAnalysis;
  if (groups.length === 0) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-gray-500">
        No duplicate gates: none of the {plural(summary.total_gates, 'gate')} has another within{' '}
        {DUPLICATE_GATE_THRESHOLD_M} m of it.
      </div>
    );
  }

  return (
    <div className="cr-flex cr-flex-col cr-gap-3">
      <div className="cr-rounded-lg cr-border cr-border-amber-200 cr-bg-amber-50 cr-p-4">
        <p className="cr-text-sm cr-font-semibold cr-text-amber-800">
          {plural(summary.total_duplicate_groups, 'duplicate group')} across{' '}
          {plural(summary.total_duplicate_gates, 'gate')}, used by{' '}
          {plural(summary.total_sessions_affected, 'session')}
        </p>
        <p className="cr-mt-1 cr-text-xs cr-text-amber-700">
          Gates whose start and end markers both sit within {DUPLICATE_GATE_THRESHOLD_M} m of each other.
          Merging a group keeps the gate with the most sessions, moves the other gates&apos; sessions onto it
          and deletes them. Opening a group shows it on the map.
        </p>
      </div>

      {groups.map((group: DuplicateGroup, groupIdx: number) => {
        const isExpanded = expandedGroups.has(groupIdx);
        const kept = keptGate(group);
        const { title, place, detail } = describeDuplicateGroup(group);

        return (
          <div
            key={kept.id}
            className="cr-overflow-hidden cr-rounded-lg cr-border cr-border-amber-200"
          >
            <div className="cr-flex cr-items-center cr-bg-amber-50 cr-transition-colors hover:cr-bg-amber-100">
              <button
                onClick={() => onToggleGroup(groupIdx)}
                className="cr-flex cr-flex-1 cr-items-center cr-gap-3 cr-p-4 cr-text-left"
              >
                <HiOutlineChevronRight
                  className={
                    'cr-h-5 cr-w-5 cr-shrink-0 cr-text-amber-600 cr-transition-transform' +
                    (isExpanded ? ' cr-rotate-90' : '')
                  }
                />
                <div className="cr-min-w-0">
                  <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
                    <span className="cr-text-lg cr-font-semibold cr-text-amber-900">{title}</span>
                    {place && <span className="cr-text-sm cr-text-amber-800">{place}</span>}
                  </div>
                  <p className="cr-text-xs cr-text-amber-700">{detail}</p>
                </div>
              </button>
              <div className="cr-px-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMergeGroup(group);
                  }}
                  disabled={isMerging}
                  title={`Keep "${title}" and merge the other ${plural(group.gate_count - 1, 'gate')} into it`}
                  className={
                    'cr-whitespace-nowrap cr-rounded-md cr-bg-amber-600 cr-px-3 cr-py-1.5 cr-text-xs ' +
                    'cr-font-semibold cr-text-white cr-shadow-sm hover:cr-bg-amber-700 disabled:cr-opacity-50'
                  }
                >
                  {isMerging ? 'Merging...' : `Merge ${group.gate_count} into 1`}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="cr-border-t cr-border-amber-200">
                {group.gates.map((gate) => (
                  <DuplicateGateRow
                    key={gate.id}
                    gate={gate}
                    isKept={gate.id === kept.id}
                    order={gateOrder.get(gate.id)}
                    isActive={gate.id === activeGateId}
                    onFocus={() => onFocusGate(gate.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** One gate inside a duplicate group: what it is, and whether a merge keeps it. */
function DuplicateGateRow({
  gate,
  isKept,
  order,
  isActive,
  onFocus,
}: {
  gate: DuplicateGateEntry;
  isKept: boolean;
  order: number | undefined;
  isActive: boolean;
  onFocus: () => void;
}) {
  const sessions = `${gate.session_count} session${gate.session_count !== 1 ? 's' : ''}`;
  const bearing =
    gate.route_bearing != null
      ? Math.round(gate.route_bearing < 0 ? gate.route_bearing + 360 : gate.route_bearing)
      : null;
  const place = [gate.route_description, gate.address].filter(Boolean).join(' · ');

  return (
    <div
      onClick={onFocus}
      className={
        'cr-flex cr-cursor-pointer cr-items-start cr-justify-between cr-gap-3 cr-border-b ' +
        'cr-border-amber-100 cr-p-4 cr-transition-colors last:cr-border-b-0 hover:cr-bg-amber-25' +
        (isActive ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      <div className="cr-min-w-0 cr-pl-7">
        <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
          <GateNumber order={order} />
          {gate.pinned && <HiStar className="cr-h-4 cr-w-4 cr-text-amber-400" title="Starred" />}
          <span className={'cr-text-sm cr-font-medium' + (gate.name ? '' : ' cr-text-gray-500')}>
            {gate.name || `Gate ${gate.id.slice(0, 8)}...`}
          </span>
          {isKept ? (
            <span
              title="A merge keeps this gate: it has the most sessions"
              className={
                'cr-rounded-full cr-bg-green-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-green-800'
              }
            >
              Kept · {sessions}
            </span>
          ) : (
            <span
              title="A merge deletes this gate and moves its sessions to the kept one"
              className={
                'cr-rounded-full cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-gray-600'
              }
            >
              Deleted on merge · {sessions}
            </span>
          )}
        </div>
        {place && <p className="cr-text-xs cr-text-gray-500">{place}</p>}
        <p className="cr-text-xs cr-text-gray-500">
          Start: [{gate.gates_lat[0]?.toFixed(6)}, {gate.gates_lon[0]?.toFixed(6)}]
          {' | '}End: [{gate.gates_lat[1]?.toFixed(6)}, {gate.gates_lon[1]?.toFixed(6)}]
        </p>
        {gate.route_distance != null && (
          <p className="cr-text-xs cr-text-gray-500">
            Distance: {Math.round(gate.route_distance)}m
            {bearing != null && <>{' | '}Bearing: {bearing}°</>}
          </p>
        )}
        {gate.sessions.length > 0 && (
          <div className="cr-mt-2 cr-pl-2">
            {gate.sessions.slice(0, 5).map((session, idx) => (
              <p key={idx} className="cr-text-xs cr-text-gray-600">
                {formatSessionDate(session.date)} - {session.description}
              </p>
            ))}
            {gate.sessions.length > 5 && (
              <p className="cr-text-xs cr-italic cr-text-gray-500">
                ...and {gate.sessions.length - 5} more
              </p>
            )}
          </div>
        )}
      </div>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onFocus();
        }}
      >
        Center
      </Button>
    </div>
  );
}
